import React, { Fragment, useCallback, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import AddIcon from '@mui/icons-material/Add';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import TimePicker from '@mui/lab/TimePicker';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';
import AuthProvider from '../Authentication/authProvider';
import SendTemplateDialog from '../../themes/components/SendTemplateDialog';
import PageShell from '../../themes/components/PageShell';
import PageHeader from '../../themes/components/PageHeader';
import PageContent from '../../themes/components/PageContent';
import Action from '../../themes/components/Action';
import ResponsiveDataGrid from '../../themes/components/ResponsiveDataGrid';
import Form from '../../themes/components/Form';
import FormSection from '../../themes/components/FormSection';
import FormActions from '../../themes/components/FormActions';
import { GetIncidentReports, AddIncidentReport, GetIncidentReportById, UpdateIncidentReport } from '../../REST/incidentReports';

// ─── List ────────────────────────────────────────────────────────────────────

function IncidentReportList() {
    const navigate = useNavigate();

    const columns = [
        { field: 'dateCallReceived', headerName: 'Date', flex: 0.4, minVisible: 'xs', valueGetter: (p) => p.row.dateCallReceived ? new Date(p.row.dateCallReceived).toLocaleDateString() : '' },
        { field: 'callReceivedBy',   headerName: 'Received By',    flex: 0.4, minVisible: 'xs' },
        { field: 'pilotName',        headerName: 'Pilot Name',      flex: 0.5, minVisible: 'sm' },
        { field: 'aircraftN',        headerName: 'N#',              flex: 0.3, minVisible: 'sm' },
        { field: 'aircraftMake',     headerName: 'Aircraft Make',   flex: 0.4, minVisible: 'md' },
        { field: 'reportCompletedBy',headerName: 'Completed By',    flex: 0.4, minVisible: 'md' },
    ];

    const fetchReports = useCallback(async (page, limit, filters, sort) => {
        const params = { page, limit };
        if (Object.keys(filters).length > 0) params.filters = JSON.stringify(filters);
        if (sort) params.sort = JSON.stringify(sort);

        const result = await GetIncidentReports(params);
        if (result.data?.data) {
            return {
                data: result.data.data.map(e => ({ ...e, id: e._id })),
                pagination: result.data.pagination
            };
        }
        return { data: [], pagination: { total: 0 } };
    }, []);

    return (
        <PageShell>
            <PageHeader
                title="Incident Reports"
                back
                onBack={() => navigate('/')}
                actions={
                    <Action
                        primary
                        icon={<AddIcon />}
                        label="New Incident Report"
                        onClick={() => navigate('/incident-reports/create')}
                    />
                }
            />
            <PageContent fill>
                <ResponsiveDataGrid
                    columns={columns}
                    fetchData={fetchReports}
                    getRowId={(row) => row._id || row.id}
                    onRowClick={(row) => navigate('/incident-reports/' + (row._id || row.id))}
                    initialPageSize={10}
                />
            </PageContent>
        </PageShell>
    );
}

// ─── Shared form schema ───────────────────────────────────────────────────────

const schema = yup.object({
    dateCallReceived:      yup.date().required(),
    timeCallReceived:      yup.date().nullable().transform((v) => (v instanceof Date && !isNaN(v) ? v : null)),
    callReceivedBy:        yup.string().trim(),
    cityPropertyDamaged:   yup.boolean(),
    respondingAgencies:    yup.string().trim(),
    // Vehicle & Operator
    vehicleName:           yup.string().trim(),
    vehicleAddress:        yup.string().trim(),
    vehicleCity:           yup.string().trim(),
    vehicleZip:            yup.string().trim(),
    vehiclePhone:          yup.string().trim(),
    driversLicense:        yup.string().trim(),
    vehicleInsuranceCompany: yup.string().trim(),
    vehiclePolicyNumber:   yup.string().trim(),
    // Pilot & Aircraft
    pilotName:             yup.string().trim(),
    pilotAddress:          yup.string().trim(),
    pilotCity:             yup.string().trim(),
    pilotZip:              yup.string().trim(),
    pilotPhone:            yup.string().trim(),
    pilotsLicenseNo:       yup.string().trim(),
    pilotInsuranceCompany: yup.string().trim(),
    pilotPolicyNumber:     yup.string().trim(),
    ownersName:            yup.string().trim(),
    ownerAddress:          yup.string().trim(),
    ownerCity:             yup.string().trim(),
    ownerZip:              yup.string().trim(),
    ownerPhone:            yup.string().trim(),
    witness1Name:          yup.string().trim(),
    witness1Address:       yup.string().trim(),
    witness1City:          yup.string().trim(),
    witness1Zip:           yup.string().trim(),
    witness1Phone:         yup.string().trim(),
    witness2Name:          yup.string().trim(),
    witness2Address:       yup.string().trim(),
    witness2City:          yup.string().trim(),
    witness2Zip:           yup.string().trim(),
    witness2Phone:         yup.string().trim(),
    aircraftMake:          yup.string().trim(),
    aircraftModel:         yup.string().trim(),
    aircraftN:             yup.string().trim(),
    picturesAttached:      yup.boolean(),
    separateReportAttached: yup.boolean(),
    ntsb9800ReleaseTime:   yup.string().trim(),
    description:           yup.string().trim(),
    reportCompletedBy:     yup.string().trim(),
    dateCompleted:         yup.date().nullable().transform((v) => (v instanceof Date && !isNaN(v) ? v : null)),
    reviewedBy:            yup.string().trim(),
});

const defaultValues = {
    dateCallReceived: new Date().toISOString(),
    timeCallReceived: null, callReceivedBy: '', cityPropertyDamaged: false, respondingAgencies: '',
    vehicleName: '', vehicleAddress: '', vehicleCity: '', vehicleZip: '', vehiclePhone: '',
    driversLicense: '', vehicleInsuranceCompany: '', vehiclePolicyNumber: '',
    pilotName: '', pilotAddress: '', pilotCity: '', pilotZip: '', pilotPhone: '',
    pilotsLicenseNo: '', pilotInsuranceCompany: '', pilotPolicyNumber: '',
    ownersName: '', ownerAddress: '', ownerCity: '', ownerZip: '', ownerPhone: '',
    witness1Name: '', witness1Address: '', witness1City: '', witness1Zip: '', witness1Phone: '',
    witness2Name: '', witness2Address: '', witness2City: '', witness2Zip: '', witness2Phone: '',
    aircraftMake: '', aircraftModel: '', aircraftN: '',
    picturesAttached: false, separateReportAttached: false, ntsb9800ReleaseTime: '',
    description: '', reportCompletedBy: '', dateCompleted: null, reviewedBy: '',
};

// ─── Shared form fields ───────────────────────────────────────────────────────

function IncidentFormFields({ control, errors, disabled = false }) {
    const isError = (v) => v !== undefined && v !== null && typeof v === 'object';
    const field = (name, label, props = {}) => (
        <Controller
            name={name}
            control={control}
            render={({ field: f }) => (
                <TextField
                    size="small" margin="dense" fullWidth
                    label={label}
                    disabled={disabled}
                    error={isError(errors[name])}
                    helperText={errors[name]?.message}
                    {...props}
                    {...f}
                />
            )}
        />
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            {/* ── Call Information ── */}
            <FormSection title="Call Information">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Controller name="dateCallReceived" control={control} render={({ field: f }) => (
                            <DatePicker disabled={disabled} label="Date Call Received"
                                renderInput={(params) => <TextField size="small" margin="dense" fullWidth required {...params} error={isError(errors.dateCallReceived)} helperText={errors.dateCallReceived?.message} onChange={f.onChange} />}
                                {...f} />
                        )} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Controller name="timeCallReceived" control={control} render={({ field: f }) => (
                            <TimePicker disabled={disabled} label="Time Call Received"
                                renderInput={(params) => <TextField size="small" margin="dense" fullWidth {...params} error={isError(errors.timeCallReceived)} helperText={errors.timeCallReceived?.message} onChange={f.onChange} />}
                                {...f}
                                value={f.value || null}
                                onChange={(val) => f.onChange(val)}
                            />
                        )} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>{field('callReceivedBy', 'Call Received By')}</Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Controller name="cityPropertyDamaged" control={control} render={({ field: f }) => (
                            <FormControlLabel label="City Property Damaged?" control={<Checkbox checked={!!f.value} onChange={(e) => f.onChange(e.target.checked)} disabled={disabled} />} />
                        )} />
                    </Grid>
                    <Grid item xs={12}>{field('respondingAgencies', 'Responding Agencies')}</Grid>
                </Grid>
            </FormSection>

            {/* ── Vehicle & Operator ── */}
            <FormSection title="Vehicle and Operator Information" divider>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>{field('vehicleName', 'Name')}</Grid>
                    <Grid item xs={12} sm={6}>{field('vehicleAddress', 'Address')}</Grid>
                    <Grid item xs={12} sm={4} md={3}>{field('vehicleCity', 'City')}</Grid>
                    <Grid item xs={6}  sm={4} md={2}>{field('vehicleZip', 'Zip')}</Grid>
                    <Grid item xs={6}  sm={4} md={3}>{field('vehiclePhone', 'Phone')}</Grid>
                    <Grid item xs={12} sm={4}>{field('driversLicense', "Driver's License")}</Grid>
                    <Grid item xs={12} sm={4}>{field('vehicleInsuranceCompany', 'Insurance Company')}</Grid>
                    <Grid item xs={12} sm={4}>{field('vehiclePolicyNumber', 'Policy Number')}</Grid>
                </Grid>
            </FormSection>

            {/* ── Pilot & Aircraft ── */}
            <FormSection title="Pilot, Passenger and Aircraft Data" divider>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>{field('pilotName', 'Name of Pilot')}</Grid>
                    <Grid item xs={12} sm={6}>{field('pilotAddress', 'Address')}</Grid>
                    <Grid item xs={6}  sm={3}>{field('pilotCity', 'City')}</Grid>
                    <Grid item xs={6}  sm={3}>{field('pilotZip', 'Zip Code')}</Grid>
                    <Grid item xs={12} sm={3}>{field('pilotPhone', 'Phone')}</Grid>
                    <Grid item xs={12} sm={4}>{field('pilotsLicenseNo', "Pilot's License No.")}</Grid>
                    <Grid item xs={12} sm={4}>{field('pilotInsuranceCompany', 'Insurance Company')}</Grid>
                    <Grid item xs={12} sm={4}>{field('pilotPolicyNumber', 'Policy Number')}</Grid>
                </Grid>
            </FormSection>

            {/* ── Tenant ── */}
            <FormSection title="Tenant Information" divider>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>{field('ownersName', "Tenant's Name")}</Grid>
                    <Grid item xs={12} sm={6}>{field('ownerAddress', 'Address')}</Grid>
                    <Grid item xs={6}  sm={3}>{field('ownerCity', 'City')}</Grid>
                    <Grid item xs={6}  sm={3}>{field('ownerZip', 'Zip Code')}</Grid>
                    <Grid item xs={12} sm={3}>{field('ownerPhone', 'Phone')}</Grid>
                </Grid>
            </FormSection>

            {/* ── Witnesses ── */}
            <FormSection title="Witnesses" divider>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>{field('witness1Name', 'Witness 1 Name')}</Grid>
                    <Grid item xs={12} sm={6}>{field('witness1Address', 'Address')}</Grid>
                    <Grid item xs={6}  sm={3}>{field('witness1City', 'City')}</Grid>
                    <Grid item xs={6}  sm={3}>{field('witness1Zip', 'Zip')}</Grid>
                    <Grid item xs={12} sm={3}>{field('witness1Phone', 'Phone')}</Grid>
                    <Grid item xs={12} sm={6}>{field('witness2Name', 'Witness 2 Name')}</Grid>
                    <Grid item xs={12} sm={6}>{field('witness2Address', 'Address')}</Grid>
                    <Grid item xs={6}  sm={3}>{field('witness2City', 'City')}</Grid>
                    <Grid item xs={6}  sm={3}>{field('witness2Zip', 'Zip')}</Grid>
                    <Grid item xs={12} sm={3}>{field('witness2Phone', 'Phone')}</Grid>
                </Grid>
            </FormSection>

            {/* ── Aircraft ── */}
            <FormSection title="Aircraft" divider>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>{field('aircraftMake', 'Aircraft Make')}</Grid>
                    <Grid item xs={12} sm={4}>{field('aircraftModel', 'Model')}</Grid>
                    <Grid item xs={12} sm={4}>{field('aircraftN', 'N#')}</Grid>
                    <Grid item xs={6} sm={3}>
                        <Controller name="picturesAttached" control={control} render={({ field: f }) => (
                            <FormControlLabel label="Pictures Attached" control={<Checkbox checked={!!f.value} onChange={(e) => f.onChange(e.target.checked)} disabled={disabled} />} />
                        )} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Controller name="separateReportAttached" control={control} render={({ field: f }) => (
                            <FormControlLabel label="Separate Report Attached" control={<Checkbox checked={!!f.value} onChange={(e) => f.onChange(e.target.checked)} disabled={disabled} />} />
                        )} />
                    </Grid>
                    <Grid item xs={12} sm={6}>{field('ntsb9800ReleaseTime', '#9800/NTSB Release Time')}</Grid>
                </Grid>
            </FormSection>

            {/* ── Incident Description ── */}
            <FormSection title="Incident Description" divider>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {field('description', 'Describe the incident (include location and damage)', { multiline: true, minRows: 4 })}
                    </Grid>
                </Grid>
            </FormSection>

            {/* ── Report Completion ── */}
            <FormSection title="Report Completion" divider>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>{field('reportCompletedBy', 'Report Completed By')}</Grid>
                    <Grid item xs={12} sm={4}>
                        <Controller name="dateCompleted" control={control} render={({ field: f }) => (
                            <DatePicker disabled={disabled} label="Date Completed"
                                renderInput={(params) => <TextField size="small" margin="dense" fullWidth {...params} error={isError(errors.dateCompleted)} helperText={errors.dateCompleted?.message} onChange={f.onChange} />}
                                {...f} />
                        )} />
                    </Grid>
                    <Grid item xs={12} sm={4}>{field('reviewedBy', 'Reviewed By')}</Grid>
                </Grid>
            </FormSection>
        </LocalizationProvider>
    );
}

// ─── Create ───────────────────────────────────────────────────────────────────

function CreateIncidentReport() {
    const navigate = useNavigate();
    const methods = useForm({ resolver: yupResolver(schema), defaultValues });
    const { control, formState: { errors } } = methods;

    const onSubmit = async (data) => {
        await AddIncidentReport(data);
        navigate('/incident-reports');
    };

    return (
        <PageShell>
            <PageHeader title="New Incident Report" back onBack={() => navigate('/incident-reports')} />
            <PageContent sx={{ p: { xs: 2, md: 3 } }}>
                <Form methods={methods} onSubmit={onSubmit}>
                    <IncidentFormFields control={control} errors={errors} />
                    <FormActions>
                        <Button onClick={() => navigate('/incident-reports')}>Cancel</Button>
                        <Button variant="contained" type="submit" startIcon={<SaveIcon />}>Submit Report</Button>
                    </FormActions>
                </Form>
            </PageContent>
        </PageShell>
    );
}

// ─── View / Edit ─────────────────────────────────────────────────────────────

function ViewIncidentReport() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [editMode, setEditMode] = useState(false);
    const [sendEmailOpen, setSendEmailOpen] = useState(false);
    const methods = useForm({ resolver: yupResolver(schema), defaultValues });
    const { control, formState: { errors }, reset } = methods;

    React.useEffect(() => {
        GetIncidentReportById(id).then(res => {
            if (res.data) reset(res.data);
        });
    }, [id, reset]);

    const onSubmit = async (data) => {
        await UpdateIncidentReport(id, data);
        setEditMode(false);
    };

    return (
        <PageShell>
            <PageHeader
                title="Incident Report"
                back
                onBack={() => navigate('/incident-reports')}
                actions={
                    <>
                        <Button variant="outlined" startIcon={<EmailIcon />} onClick={() => setSendEmailOpen(true)}>Send Email</Button>
                        {!editMode && <Button variant="outlined" onClick={() => setEditMode(true)}>Edit</Button>}
                    </>
                }
            />
            <PageContent sx={{ p: { xs: 2, md: 3 } }}>
                <Form methods={methods} onSubmit={onSubmit}>
                    <IncidentFormFields control={control} errors={errors} disabled={!editMode} />
                    {editMode && (
                        <FormActions>
                            <Button onClick={() => setEditMode(false)}>Cancel</Button>
                            <Button variant="contained" type="submit" startIcon={<SaveIcon />}>Save</Button>
                        </FormActions>
                    )}
                </Form>
            </PageContent>
            <SendTemplateDialog
                open={sendEmailOpen}
                onClose={() => setSendEmailOpen(false)}
                module="IncidentReports"
                entityId={id}
                defaultEmail=''
            />
        </PageShell>
    );
}

// ─── Router ───────────────────────────────────────────────────────────────────

export default function IncidentReport() {
    const location = useLocation();
    return (
        <Fragment>
            {location.pathname === '/incident-reports' && <IncidentReportList />}
            <Routes>
                <Route path="create" element={<AuthProvider><CreateIncidentReport /></AuthProvider>} />
                <Route path=":id"     element={<AuthProvider><ViewIncidentReport /></AuthProvider>} />
            </Routes>
        </Fragment>
    );
}
